import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from 'react';
import { defaultSiteContent, getFeaturedServices, getServiceOptions, type SiteContent } from '../data/site';
import { fetchRemoteContent, saveRemoteContent } from '../lib/cms';
import { isSupabaseConfigured } from '../lib/supabase';

const STORAGE_KEY = 'bbsite-content';

type ContentContextValue = {
  content: SiteContent;
  featuredServices: ReturnType<typeof getFeaturedServices>;
  serviceOptions: string[];
  contentSource: 'local' | 'supabase';
  isLoadingContent: boolean;
  saveContent: (nextContent: SiteContent) => Promise<void>;
  resetContent: () => Promise<void>;
};

const ContentContext = createContext<ContentContextValue | null>(null);

function safelyParseContent(value: string): SiteContent | null {
  try {
    const parsed = JSON.parse(value) as SiteContent;

    if (!parsed || typeof parsed !== 'object' || !parsed.site || !Array.isArray(parsed.serviceGroups)) {
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
}

export function ContentProvider({ children }: PropsWithChildren) {
  const [content, setContent] = useState<SiteContent>(defaultSiteContent);
  const [contentSource, setContentSource] = useState<'local' | 'supabase'>('local');
  const [isLoadingContent, setIsLoadingContent] = useState(true);

  useEffect(() => {
    let isCancelled = false;

    const loadContent = async () => {
      const stored = window.localStorage.getItem(STORAGE_KEY);
      const parsedLocal = stored ? safelyParseContent(stored) : null;

      if (parsedLocal && !isCancelled) {
        setContent(parsedLocal);
        setContentSource('local');
      }

      if (!isSupabaseConfigured) {
        if (!isCancelled) {
          setIsLoadingContent(false);
        }
        return;
      }

      try {
        const remoteContent = await fetchRemoteContent();

        if (remoteContent && !isCancelled) {
          setContent(remoteContent);
          setContentSource('supabase');
        }
      } catch {
        if (!isCancelled && !parsedLocal) {
          setContent(defaultSiteContent);
        }
      } finally {
        if (!isCancelled) {
          setIsLoadingContent(false);
        }
      }
    };

    loadContent();

    return () => {
      isCancelled = true;
    };
  }, []);

  const value = useMemo<ContentContextValue>(
    () => ({
      content,
      featuredServices: getFeaturedServices(content),
      serviceOptions: getServiceOptions(content),
      contentSource,
      isLoadingContent,
      saveContent: async (nextContent) => {
        setContent(nextContent);
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(nextContent));

        if (isSupabaseConfigured) {
          await saveRemoteContent(nextContent);
          setContentSource('supabase');
        } else {
          setContentSource('local');
        }
      },
      resetContent: async () => {
        setContent(defaultSiteContent);
        window.localStorage.removeItem(STORAGE_KEY);

        if (isSupabaseConfigured) {
          await saveRemoteContent(defaultSiteContent);
          setContentSource('supabase');
        } else {
          setContentSource('local');
        }
      },
    }),
    [content, contentSource, isLoadingContent],
  );

  return <ContentContext.Provider value={value}>{children}</ContentContext.Provider>;
}

export function useSiteContent() {
  const context = useContext(ContentContext);

  if (!context) {
    throw new Error('useSiteContent must be used inside ContentProvider');
  }

  return context;
}
